import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AutoRedemptionRCSpecificAmountFormComponent } from '@features/auto-redemption-settings-ui/components/auto-redemption-rc-specific-amount-form/auto-redemption-specific-amount-form.component';
import { AutoRedemptionSettingsViewFacade } from '@features/auto-redemption-settings-ui/facades/auto-redemption-settings-view.facade';
import { AutoRedemptionSettingFacade } from '@features/auto-redemption-settings/facades/auto-redemption-setting/auto-redemption-setting.facade';
import { CreateCashBackAutoRedemptionSetting } from '@features/auto-redemption-settings/services/auto-redemption-setting-api/auto-redemption-setting-api.service';
import { CashbackFacade } from '@features/cashback/facades/cashback.facade';
import { EnrollmentFacade } from '@features/enrollment/facades/enrollment.facade';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import { Store } from '@ngrx/store';
import { getAuthUser } from '@rd/core/auth/store/selectors/auth.selectors';
import { LoyaltyCurrencyFacade } from '@rd/core/facades/loyalty-currency-facade/loyalty-currency.facade';
import { TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import {
  CashBackMembership,
  CashBackToAccountMembership,
  CashBackToCardMembership,
  Enrollment,
  LoyaltyCurrency,
  LoyaltyProgram,
  LoyaltyProgramCategory,
  MembershipType,
  User,
} from '@rd/types';
import { CashBackAutoRedemptionType } from '@rd/types/auto-redemption-setting.model';
import {
  AutoRedemptionSettingType,
  CashRedemptionAutoRedemptionSetting,
} from '@rd/types/auto-redemption-setting.type';
import { isNotNull } from '@rd/utils/isNotNull';
import {
  ANZEnrollmentFormComponentComponent,
  EnrollmentFormOptions,
} from '@rd/wl/anz/components/enrollment-form-component/enrollment-form.component';
import { EnrollmentFormType } from '@rd/wl/anz/types';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'rd-anz-auto-redemption-rc-enrollment',
  templateUrl: './auto-redemption-enrollment.component.html',
  styleUrls: ['./auto-redemption-enrollment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ANZAutoredemptionEnrollmentComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  get form(): UntypedFormGroup {
    return this.component?.form;
  }

  get enrollmentForm(): UntypedFormGroup {
    return this.form.controls.enrollment as UntypedFormGroup;
  }

  get bankAccountForm(): UntypedFormGroup {
    return this.form.controls.accountDetails as UntypedFormGroup;
  }

  get selectedOption(): UntypedFormControl {
    return this.form.get('selectedOption') as UntypedFormControl;
  }

  destroy$: Subject<void> = new Subject<void>();

  constructor(
    @Inject(TENANT_NAME) private tenantName: string,
    public membershipFacade: MembershipFacade,
    public autoRedemptionSettingFacade: AutoRedemptionSettingFacade,
    public loyaltyProgramFacade: LoyaltyProgramFacade,
    public loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    public enrollmentFacade: EnrollmentFacade,
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private autoRedemptionSettingsViewFacade: AutoRedemptionSettingsViewFacade,
    private cashbackFacade: CashbackFacade,
  ) {}

  cashBackFormData$ = this.cashbackFacade.cashbackForm$;

  @ViewChild(ANZEnrollmentFormComponentComponent, { static: true })
  component: ANZEnrollmentFormComponentComponent;

  @ViewChild(AutoRedemptionRCSpecificAmountFormComponent)
  rcForm: AutoRedemptionRCSpecificAmountFormComponent;

  readonly enrollmentFormType = EnrollmentFormType;

  formType$: Observable<EnrollmentFormType>;

  disableButtonState$: Observable<boolean> =
    this.membershipFacade.createOrUpdateMembershipLoading$;

  cashBackToCardLoyaltyProgram$ =
    this.loyaltyProgramFacade.cashBackToCardLoyaltyProgram$.pipe(
      filter(isNotNull),
    );

  cashBackToAccountLoyaltyProgram$: Observable<LoyaltyProgram> =
    this.loyaltyProgramFacade.cashBackToAccountLoyaltyProgram$.pipe(
      filter(isNotNull),
    );

  cashBackToAccountMembership$: Observable<CashBackToAccountMembership | null> =
    this.membershipFacade.cashBackToAccountMembership$;

  cashBackToCardMembership$: Observable<CashBackToCardMembership | null> =
    this.membershipFacade.cashBackToCardMembership$;

  cashBackLoyaltyProgram$: BehaviorSubject<LoyaltyProgram | null> =
    new BehaviorSubject<LoyaltyProgram | null>(null);

  homeLoyaltyCurrency$: Observable<LoyaltyCurrency> =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$.pipe(
      filter(isNotNull),
    );

  error$: Observable<boolean> =
    this.membershipFacade.createOrUpdateMembershipError$;

  user$: Observable<User> = this.store
    .select(getAuthUser)
    .pipe(filter(isNotNull));

  ngAfterViewInit(): void {
    this.cashBackFormData$
      .pipe(
        take(1),
        tap((data) => {
          if (data) {
            this.rcForm?.specificAmountForm.patchValue({
              pointsAmount: data.targetAmount,
            });
          }
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.autoRedemptionSettingsViewFacade.enterDetailsPage(
      AutoRedemptionSettingType.Cash,
    );

    // Default loyalty program to cashback to card
    this.cashBackToCardLoyaltyProgram$
      .pipe(
        take(1),
        tap((lp) => this.cashBackLoyaltyProgram$.next(lp)),
      )
      .subscribe();
    this.formType$ = this.component.formType$;
    // This is to setup which loyalty program is used
    // given that cashback to card and cash back to account are different
    this.selectedOption.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        switchMap((selectedOption) => {
          if (this.tenantName === 'anz-biz') {
            // ANZ Biz doesn't have cash back to account
            return this.cashBackToCardLoyaltyProgram$;
          }
          if (selectedOption === EnrollmentFormOptions.BankAccount) {
            return this.cashBackToAccountLoyaltyProgram$;
          }
          return this.cashBackToCardLoyaltyProgram$;
        }),
        tap((a) => this.cashBackLoyaltyProgram$.next(a)),
        tap(() => this.cdRef.detectChanges()),
      )
      .subscribe();

    combineLatest([
      this.autoRedemptionSettingFacade.autoRedemptionSetting$.pipe(
        map((ar) => {
          switch (ar?.type) {
            case AutoRedemptionSettingType.Cash: {
              return ar as CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType>;
            }
          }

          return null;
        }),
      ),
      this.membershipFacade.preferredCashBackMembership$,
      this.enrollmentFacade.enrollments$,
      this.formType$,
      this.user$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        tap(
          ([
            autoRedemptionSetting,
            preferredCashBackMembership,
            enrollments,
            formType,
            user,
          ]: [
            (
              | CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType.CashBackToAccount>
              | CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType.CashBackToCard>
              | null
            ),
            CashBackMembership | null,
            Enrollment[],
            EnrollmentFormType,
            User,
          ]) => {
            if (autoRedemptionSetting) {
              if (
                this.isCashBackToCardAutoRedemption(autoRedemptionSetting) &&
                !this.isCashBackToAccountAutoRedemption(autoRedemptionSetting)
              ) {
                // if there is an auto redemption, there must be enrollments
                this.component.form.patchValue({
                  selectedOption: this.component.formOptions.CreditCard,
                  enrollment: {
                    selected:
                      enrollments.find(
                        (enrollment) =>
                          enrollment.id ===
                          autoRedemptionSetting.data.enrollmentId,
                      ) ?? enrollments[0],
                  },
                  accountDetails: {
                    accountFirstName: user.firstName ?? '',
                    accountLastName: user.lastName ?? '',
                    accountNumber: '',
                    bsbNumber: '',
                  },
                });
                return;
              }

              if (
                this.isCashBackToAccountAutoRedemption(autoRedemptionSetting)
              ) {
                this.component.form.patchValue({
                  selectedOption: this.component.formOptions.BankAccount,
                  accountDetails: {
                    accountFirstName: autoRedemptionSetting.data.firstName,
                    accountLastName: autoRedemptionSetting.data.lastName,
                    accountNumber: autoRedemptionSetting.data.number,
                    bsbNumber: autoRedemptionSetting.data.details.bsbNumber,
                  },
                });

                if (enrollments.length > 0) {
                  this.component.form.patchValue({
                    enrollment: {
                      selected: enrollments[0],
                    },
                  });
                }
                return;
                // Prevent pre-filling from user information
              }
              // Auto redemption but not Cashback, do what?
              this.prefillFormData(formType, enrollments, user);
              return;
            }

            // No Auto Redemptions
            // Check memberships
            if (preferredCashBackMembership) {
              if (
                this.isMembershipCashBackToCard(preferredCashBackMembership)
              ) {
                const selectedEnrollment =
                  enrollments.find(
                    (enrollment) =>
                      enrollment.id ===
                      preferredCashBackMembership.enrollmentId,
                  ) ?? enrollments[0];
                // Prefill bank account form just in case
                this.component.form.patchValue({
                  selectedOption: this.component.formOptions.CreditCard,
                  enrollment: {
                    selected: selectedEnrollment,
                  },
                  accountDetails: {
                    // Prefill bank account info from token
                    accountFirstName: user.firstName ?? '',
                    accountLastName: user.lastName ?? '',
                    accountNumber: '',
                    bsbNumber: '',
                  },
                });
                return;
              }

              if (
                this.isMembershipCashBackToAccount(preferredCashBackMembership)
              ) {
                if (enrollments.length > 0) {
                  this.component.form.patchValue({
                    enrollment: {
                      selected: enrollments[0],
                    },
                  });
                }

                this.component.form.patchValue({
                  selectedOption: this.component.formOptions.BankAccount,
                  accountDetails: {
                    accountFirstName: preferredCashBackMembership.firstName,
                    accountLastName: preferredCashBackMembership.lastName,
                    accountNumber: preferredCashBackMembership.number,
                    bsbNumber: preferredCashBackMembership.details.bsbNumber,
                  },
                });
              }
              return;
            }

            // No Auto Redemption and No memberships
            this.prefillFormData(formType, enrollments, user);
          },
        ),
      )
      .subscribe();

    this.autoRedemptionSettingFacade.autoRedemptionSetting$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {}),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit(): void {
    const selectedOption: EnrollmentFormOptions = this.selectedOption.value;
    switch (selectedOption) {
      case EnrollmentFormOptions.CreditCard: {
        this.enrollmentForm.markAllAsTouched();
        this.submitAutoRedemptionSettingForm(
          LoyaltyProgramCategory.CashbackToCard,
        );
        break;
      }
      case EnrollmentFormOptions.BankAccount: {
        this.bankAccountForm.markAllAsTouched();
        if (this.bankAccountForm.valid) {
          this.submitAutoRedemptionSettingForm(
            LoyaltyProgramCategory.CashbackToAccount,
          );
        }
        break;
      }
    }
  }

  private submitAutoRedemptionSettingForm(
    membershipCategory:
      | LoyaltyProgramCategory.CashbackToCard
      | LoyaltyProgramCategory.CashbackToAccount,
  ): void {
    if (membershipCategory === LoyaltyProgramCategory.CashbackToCard) {
      combineLatest(
        this.cashBackToCardMembership$,
        this.cashBackLoyaltyProgram$,
        this.enrollmentForm.valueChanges.pipe(
          startWith(this.enrollmentForm.value),
        ),
        this.user$,
        this.rcForm.formData$.pipe(filter(isNotNull)),
      )
        .pipe(
          take(1),
          tap(
            ([membership, loyaltyProgram, enrollmentForm, user, formData]) => {
              const baseData: any = {
                pointsRequired: formData.pointsRequired,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                product_id: loyaltyProgram!.id,
                displayValue: formData.displayValue,
                type: AutoRedemptionSettingType.Cash,
                strategy_options: formData.strategyOptions,
                strategy: formData.strategy,
                data: {
                  number: enrollmentForm.selected?.groupReferenceId,
                  enrollment_id: enrollmentForm.selected?.id,
                  // Have to pull this from Token since we don't collect this from form
                  firstName: user.firstName ?? '',
                  lastName: user.lastName ?? '',
                },
              };
              // membership exists, NN will pull loyalty program from membership
              if (membership !== null) {
                baseData.data.membership_id = membership.id;
                // override default name with what we got from membership
                baseData.data.firstName = membership.firstName;
                baseData.data.lastName = membership.lastName;
              } else {
                // no membership, pass loyalty program
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                baseData.data.loyalty_program_id = loyaltyProgram!.id;
              }

              return this.autoRedemptionSettingFacade.submitAutoRedemptionSettings(
                baseData as CreateCashBackAutoRedemptionSetting,
              );
            },
          ),
        )
        .subscribe();
      return;
    }

    // Cashback to account
    combineLatest(
      this.cashBackToAccountMembership$,
      this.cashBackLoyaltyProgram$,
      this.bankAccountForm.valueChanges.pipe(
        startWith(this.bankAccountForm.value),
      ),
      this.rcForm.formData$.pipe(filter(isNotNull)),
    )
      .pipe(
        take(1),
        tap(([membership, loyaltyProgram, bankForm, formData]) => {
          const baseData: any = {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            product_id: loyaltyProgram!.id,
            pointsRequired: formData.pointsRequired,
            displayValue: formData.displayValue,
            type: AutoRedemptionSettingType.Cash,
            strategy: formData.strategy,
            strategy_options: formData.strategyOptions,
            data: {
              firstName: bankForm.accountFirstName,
              lastName: bankForm.accountLastName,
              number: bankForm.accountNumber,
              details: {
                bsb_number: bankForm.bsbNumber,
              },
            },
          };

          if (membership === null) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            baseData.data.loyalty_program_id = loyaltyProgram!.id;
          } else {
            baseData.data.membership_id = membership.id;
          }
          return this.autoRedemptionSettingFacade.submitAutoRedemptionSettings(
            baseData as CreateCashBackAutoRedemptionSetting,
          );
        }),
      )
      .subscribe();
  }

  private isCashBackToAccountAutoRedemption(
    autoRedemption:
      | CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType>
      | undefined,
  ): autoRedemption is CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType.CashBackToAccount> {
    return (
      autoRedemption !== undefined &&
      autoRedemption.type === AutoRedemptionSettingType.Cash &&
      'bsbNumber' in autoRedemption.data.details &&
      !('groupName' in autoRedemption.data.details)
    );
  }

  private isCashBackToCardAutoRedemption(
    autoRedemption:
      | CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType>
      | undefined,
  ): autoRedemption is CashRedemptionAutoRedemptionSetting<CashBackAutoRedemptionType.CashBackToCard> {
    return (
      autoRedemption !== undefined &&
      autoRedemption.type === AutoRedemptionSettingType.Cash &&
      'groupName' in autoRedemption.data.details &&
      !('bsbNumber' in autoRedemption.data.details)
    );
  }

  private isMembershipCashBackToCard(
    membership: CashBackMembership,
  ): membership is CashBackToCardMembership {
    return (
      membership.type === MembershipType.CashBack &&
      membership.category === LoyaltyProgramCategory.CashbackToCard
    );
  }

  private isMembershipCashBackToAccount(
    membership: CashBackMembership,
  ): membership is CashBackToAccountMembership {
    return (
      membership.type === MembershipType.CashBack &&
      membership.category === LoyaltyProgramCategory.CashbackToAccount
    );
  }

  private prefillFormData(
    formType: EnrollmentFormType,
    enrollments: Enrollment[],
    user: User,
  ): void {
    switch (formType) {
      case EnrollmentFormType.CreditCardAndBankAccount: {
        this.component.form.patchValue({
          selectedOption: EnrollmentFormOptions.CreditCard,
          enrollment: {
            selected: enrollments[0],
          },
          accountDetails: {
            accountFirstName: user.firstName ?? '',
            accountLastName: user.lastName ?? '',
            accountNumber: '',
            bsbNumber: '',
          },
        });
        break;
      }

      case EnrollmentFormType.NoCreditCardOnlyBankAccount: {
        this.component.form.patchValue({
          selectedOption: EnrollmentFormOptions.BankAccount,
          accountDetails: {
            accountFirstName: user.firstName ?? '',
            accountLastName: user.lastName ?? '',
            accountNumber: '',
            bsbNumber: '',
          },
        });
        break;
      }

      case EnrollmentFormType.CreditCardOnly: {
        this.component.form.patchValue({
          selectedOption: EnrollmentFormOptions.CreditCard,
          enrollment: {
            selected: enrollments[0],
          },
        });
        break;
      }
    }
  }
}
